import { useSeekaConverge } from "@seeka-labs/converge-react";
import { Box, Flex, Text, LegacyText } from "flicket-ui";
import { Select } from "~components/common/Select";
import {
  alcoholFreeZoneOptions,
  coverTypeOptions,
  seatSelectTypeOptions,
  sortOptions,
} from "~components/reservation/constants";
import {
  OptionKey,
  useSeatedReservationStore,
} from "~features/seated-reservation/store";

import * as actions from "~features/seated-reservation/store/actions/index";
import { useFilterOptions } from "~hooks/useFilterOptions";

interface FiltersProps {
  amountDisabled: boolean;
  seatAmountOptions: {
    value: number;
    label: string;
  }[];
  filterOptions?: string[];
}

export const Filters = ({
  amountDisabled,
  seatAmountOptions,
  filterOptions,
}: FiltersProps) => {
  const options = useSeatedReservationStore((state) => state.options);
  const converge = useSeekaConverge();

  const updateOptions = async (key: OptionKey, value: any) => {
    await actions.updateOptions({ options: { [key]: value } }, converge);
  };

  const { hasOption } = useFilterOptions(filterOptions);

  return (
    <Box
      bg="N200"
      d={{ _: "none", md: "flex" }}
      py="6/4"
      px={4}
      w="100%"
      alignItems="center"
      justifyContent="space-between"
    >
      <Flex alignItems="center">
        <LegacyText
          color="N800"
          fontWeight="demiBold"
          mr={2}
          whiteSpace="nowrap"
          fontSize={2}
        >
          I need tickets for
        </LegacyText>
        <Box minW="124px" mr={2}>
          <Select
            // defaultValue={seatAmountOptions.find((opt) => opt.value === options.amount)}
            value={seatAmountOptions.find(
              (opt) => opt.value === options.amount
            )}
            options={seatAmountOptions}
            onChange={async (e: (typeof seatAmountOptions)[number]) =>
              updateOptions("amount", e.value)
            }
            hideSelectedOptions
            small
            isDisabled={amountDisabled}
          />
        </Box>

        {hasOption("cover") && (
          <>
            <LegacyText
              fontSize={2}
              color="N800"
              mr={1}
              whiteSpace="nowrap"
              fontWeight="demiBold"
            >
              Seat cover
            </LegacyText>
            <Box minW="124px" mr={2}>
              <Select
                value={coverTypeOptions.find(
                  (opt) => opt.value === options.coverType
                )}
                options={coverTypeOptions}
                onChange={async (e: (typeof coverTypeOptions)[number]) =>
                  updateOptions("coverType", e.value)
                }
                hideSelectedOptions
                small
                selectProps={{
                  isSearchable: false,
                }}
              />
            </Box>
          </>
        )}

        {hasOption("alcohol") && (
          <>
            <LegacyText
              fontSize={2}
              color="N800"
              mr={1}
              whiteSpace="nowrap"
              fontWeight="demiBold"
            >
              Alcohol free zone
            </LegacyText>
            <Box minW="124px" mr={2}>
              <Select
                value={alcoholFreeZoneOptions.find(
                  (option) => option.value === options.zoneType
                )}
                options={alcoholFreeZoneOptions}
                onChange={async (e: (typeof alcoholFreeZoneOptions)[number]) =>
                  updateOptions("zoneType", e.value)
                }
                hideSelectedOptions
                small
                borderRadius="6px"
                selectProps={{
                  isSearchable: false,
                }}
              />
            </Box>
          </>
        )}

        <Box pl={2} borderLeft="1px solid" borderColor="N300">
          <LegacyText
            fontSize={2}
            color="N800"
            whiteSpace="nowrap"
            mr={1}
            fontWeight="demiBold"
          >
            Sort by
          </LegacyText>
        </Box>
        <Box minW="124px">
          <Select
            value={sortOptions.find(
              (opt) => opt.value === options.selectedSort
            )}
            options={sortOptions}
            onChange={async (e: (typeof sortOptions)[number]) =>
              updateOptions("selectedSort", e.value)
            }
            hideSelectedOptions
            small
            mr={2}
            selectProps={{
              isSearchable: false,
            }}
          />
        </Box>
      </Flex>
      <Flex alignItems="center">
        <Box pl={2} borderColor="N300">
          <LegacyText
            fontSize={2}
            color="N800"
            whiteSpace="nowrap"
            mr={1}
            fontWeight="demiBold"
          >
            Seat selection
          </LegacyText>
        </Box>
        <Box minW="124px">
          <Select
            value={seatSelectTypeOptions.find(
              (opt) => opt.value === options.seatSelectType
            )}
            options={seatSelectTypeOptions}
            onChange={async (e: (typeof seatSelectTypeOptions)[number]) =>
              updateOptions("seatSelectType", e.value)
            }
            hideSelectedOptions
            small
            mr={2}
            selectProps={{
              isSearchable: false,
            }}
          />
        </Box>
      </Flex>
    </Box>
  );
};
